import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NiTextFieldComponent } from '../../app/shared/lib/ni-text-field/ni-text-field.component';
import { NiButtonComponent } from '../../app/shared/lib/ni-button/ni-button.component';
import { ParticipantCardComponent, Participant } from '../participant-card/participant-card.component';
import { DeliveryService, DeliveryQueuePayload } from '../core/services/delivery.service';
import { UserService } from '../core/services/user.service';
import { LanguageService } from '../core/services/language.service';
import { trigger, transition, style, animate } from '@angular/animations';
import { pageLoadAnimationBottomToCenter } from '../shared/utils/common-animations';
import { getUserDetails, getProgramId as getProgramIdUtil, getSelectedLocation } from '../shared/utils/storage-utils';
import { Subscription } from 'rxjs';

// Participant interface is now imported from ParticipantCardComponent

interface GroupedParticipants {
  letter: string;
  participants: Participant[];
}

interface StatusStyle {
  bg: string;
  text: string;
  border: string;
  badgeColor?: string;
}

interface StatusConfig {
  key: string;
  label: string;
  showBadge: boolean;
}

// Extended Participant interface to store userId from API
interface ExtendedParticipant extends Participant {
  userId?: number;
}

interface DeliveryToastState {
  participantId: string;
  participantName: string;
  userId: number;
  revertStatus: Participant['status'];
  message: string;
  isUndoing: boolean;
  isHiding: boolean;
  remainingSeconds: number;
  outcome: 'delivered' | 'not_delivered';
}

@Component({
  selector: 'app-check-in',
  templateUrl: './check-in.component.html',
  styleUrls: ['./check-in.component.css'],
  imports: [CommonModule, FormsModule, NiTextFieldComponent, NiButtonComponent, ParticipantCardComponent],
  animations: [
    pageLoadAnimationBottomToCenter,
    // No data animation - fade in with scale and bounce
    trigger('noDataAnimation', [
      transition(':enter', [
        style({ 
          opacity: 0, 
          transform: 'translateY(30px) scale(0.9)' 
        }),
        animate('600ms cubic-bezier(0.34, 1.56, 0.64, 1)', style({ 
          opacity: 1, 
          transform: 'translateY(0) scale(1)' 
        }))
      ])
    ])
  ]
})
export class CheckInComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('contentContainer', { static: false }) contentContainer!: ElementRef;
  
  private readonly deliveryService = inject(DeliveryService);
  private readonly userService = inject(UserService);
  private searchDebounceTimeout: any;
  userDetails: any = getUserDetails();
  pageLoadState = 'hidden';
  isLoading: boolean = true;
  searchValue: string = '';
  selectedStatus: string = 'check-in';
  selectedParticipants: string[] = [];
  activeLetter: string = 'A';
  private scrollTimeout: any;
  private isScrolling: boolean = false;
  private static fallbackIdCounter = 0;
  
  // API data
  participants: ExtendedParticipant[] = [];
  statusCounts: { [key: string]: number } = {
    'all': 0,
    'yet-to-check-in': 0,
    'check-in': 0,
    'delivered': 0,
    'not-delivered': 0,
    'waiting-list': 0,
    'no-show': 0,
    'canceled': 0
  };
  deliveringParticipantIds: Set<string> = new Set(); // Track participants being delivered
  deliveryToast: DeliveryToastState | null = null;
  /** Participant awaiting Delivered vs Not present confirmation. */
  pendingDeliveryParticipant: ExtendedParticipant | null = null;

  private deliveryToastTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private readonly deliveryToastDuration = 6000;
  private deliveryToastHideTimeoutId: ReturnType<typeof setTimeout> | null = null;
  private deliveryToastCountdownIntervalId: ReturnType<typeof setInterval> | null = null;

  alphabet: string[] = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H', 'I', 'J', 'K', 'L', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U', 'V', 'W', 'X', 'Y', 'Z'];
  readonly skeletonCardPlaceholders: number[] = Array.from({ length: 8 }, (_, index) => index);
  readonly skeletonFilterPlaceholders: number[] = Array.from({ length: 6 }, (_, index) => index);

  private readonly languageService = inject(LanguageService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private languageSub?: Subscription;
  deliveryType: 'meal' | 'transport' | 'activity' = 'meal';
  selectedActivityId: string | null = null;
  selectedActivityName: string | null = null;

  get lunchTitle(): string {
    if (this.deliveryType === 'transport') {
      return this.languageService.t('checkin.transport');
    }
    if (this.deliveryType === 'activity') {
      return this.selectedActivityName?.trim()
        || this.languageService.t('checkin.activity');
    }
    return this.languageService.t('checkin.lunch');
  }

  get deliveryHint(): string {
    if (this.deliveryType === 'transport') {
      return this.languageService.t('checkin.hint.transportDeliver');
    }
    if (this.deliveryType === 'activity') {
      return this.languageService.t('checkin.hint.activityDeliver');
    }
    return this.languageService.t('checkin.hint.mealDeliver');
  }

  get deliveryConfirmTitle(): string {
    return this.languageService.t('checkin.action.confirmTitle');
  }

  get deliveryConfirmBody(): string {
    const name = this.pendingDeliveryParticipant?.name?.trim() || 'this participant';
    return this.languageService.t('checkin.action.confirmBody', { name });
  }

  get markDeliveredLabel(): string {
    return this.languageService.t('checkin.action.markDelivered');
  }

  get markNotPresentLabel(): string {
    return this.languageService.t('checkin.action.markNotPresent');
  }

  get cancelDeliveryActionLabel(): string {
    return this.languageService.t('checkin.action.cancel');
  }

  get searchPlaceholder(): string {
    return this.languageService.t('common.search');
  }

  get backLabel(): string {
    return this.languageService.t('common.back');
  }

  goBack(): void {
    void this.router.navigate(['/staff-delivery-layout']);
  }

  /**
   * Stable list for *ngFor — must NOT be a getter that allocates a new array each CD
   * cycle (that recreates NiButtons and eats filter clicks).
   */
  statusConfigs: StatusConfig[] = [];

  private rebuildStatusConfigs(): void {
    this.statusConfigs = [
      { key: 'all', label: this.languageService.t('checkin.status.all'), showBadge: false },
      { key: 'yet-to-check-in', label: this.languageService.t('checkin.status.yetToCheckIn'), showBadge: true },
      { key: 'check-in', label: this.languageService.t('checkin.status.checkIn'), showBadge: true },
      { key: 'waiting-list', label: this.languageService.t('checkin.status.waitingList'), showBadge: true },
      { key: 'delivered', label: this.languageService.t('checkin.status.delivered'), showBadge: true },
      { key: 'not-delivered', label: this.languageService.t('checkin.status.notDelivered'), showBadge: true },
      { key: 'no-show', label: this.languageService.t('checkin.status.noShow'), showBadge: true },
      { key: 'canceled', label: this.languageService.t('checkin.status.canceled'), showBadge: true },
    ];
  }

  trackByStatusKey(_index: number, statusConfig: StatusConfig): string {
    return statusConfig.key;
  }

  /**
   * Helper method to get programId from sessionStorage
   */
  private getProgramId(): number | null {
    return getProgramIdUtil();
  }

  ngOnInit(): void {
    const routeDeliveryType = this.route.snapshot.data['deliveryType'];
    if (routeDeliveryType === 'transport') {
      this.deliveryType = 'transport';
    } else if (routeDeliveryType === 'activity') {
      this.deliveryType = 'activity';
    } else {
      this.deliveryType = 'meal';
    }

    const activityIdParam = this.route.snapshot.paramMap.get('activityId')?.trim();
    this.selectedActivityId = activityIdParam || null;
    const navState = history.state as { activityName?: string } | null;
    this.selectedActivityName = navState?.activityName?.trim() || null;

    this.rebuildStatusConfigs();
    this.languageSub = this.languageService.language$.subscribe(() => {
      this.rebuildStatusConfigs();
      this.cdr.markForCheck();
    });
    const programId = this.getProgramId();
    if (programId) {
      this.fetchDeliveryQueue();
      this.fetchStatusCounts();
    } else {
      this.isLoading = false;
    }
  }

  ngAfterViewInit(): void {
    // Trigger animation immediately after view init
    requestAnimationFrame(() => {
      setTimeout(() => {
        this.pageLoadState = 'visible';
      }, 50);
    });
    // Set initial active letter after view init
    setTimeout(() => {
      this.updateActiveLetter();
    }, 200);
  }

  ngOnDestroy(): void {
    this.languageSub?.unsubscribe();
    // Cleanup scroll timeout
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    // Cleanup search debounce timeout
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }
    this.clearDeliveryToastTimeout();
    this.clearDeliveryToastHideTimeout();
    this.clearDeliveryToastCountdown();
  }

  /**
   * Maps component status to API statusFilter
   */
  private mapStatusToApiFilter(status: string): string {
    const statusMap: { [key: string]: string } = {
      'all': 'all',
      'yet-to-check-in': 'yet_to_checkin',
      'check-in': 'check-in',
      'delivered': 'delivered',
      'not-delivered': 'not_delivered',
      'waiting-list': 'pending',
      'no-show': 'no_show',
      'canceled': 'canceled'
    };
    return statusMap[status] ?? 'pending';
  }

  /**
   * Extracts a status string from any of the API's loosely typed fields.
   * Handles both string values and nested objects that may contain the actual status text.
   */
  private extractStatusString(source: any): string | undefined {
    if (!source) {
      return undefined;
    }

    if (typeof source === 'string') {
      return source;
    }

    if (typeof source === 'object') {
      const candidateKeys = [
        'status', 'statusName', 'statusLabel', 'status_code', 'statusCode',
        'statusDescription', 'name', 'label', 'value', 'title', 'description', 'code'
      ];

      for (const key of candidateKeys) {
        if (source[key]) {
          const nested = this.extractStatusString(source[key]);
          if (nested) {
            return nested;
          }
        }
      }
    }

    return undefined;
  }

  /**
   * Pulls the best-effort raw status value from the queue item.
   */
  private resolveRawStatus(item: any): string | undefined {
    if (!item) {
      return undefined;
    }

    const directFields = [
      'status', 'statusName', 'statusLabel', 'status_code', 'statusCode',
      'statusDescription', 'queueStatus', 'deliveryStatus', 'currentStatus', 'statusType'
    ];

    for (const field of directFields) {
      if (item[field]) {
        const extracted = this.extractStatusString(item[field]);
        if (extracted) {
          return extracted;
        }
      }
    }

    return this.extractStatusString(item.status);
  }

  /**
   * Normalizes the various API status strings to our Participant status union.
   * Handles different casings, spacing, underscores, etc.
   */
  private normalizeParticipantStatus(rawStatus: any): Participant['status'] {
    if (!rawStatus) {
      return 'yet-to-check-in';
    }

    const compactStatus = rawStatus
      .toString()
      .trim()
      .toLowerCase()
      .replace(/[\s_-]/g, '');

    const statusMap: Record<string, Participant['status']> = {
      checkin: 'check-in',
      checkedin: 'check-in',
      checkined: 'check-in',
      delivered: 'delivered',
      deliveredstatus: 'delivered',
      mealdelivered: 'delivered',
      completed: 'delivered',
      complete: 'delivered',
      notdelivered: 'not-delivered',
      notpresent: 'not-delivered',
      waitinglist: 'waiting-list',
      waitlist: 'waiting-list',
      waiting: 'waiting-list',
      pendingwaiting: 'waiting-list',
      pending: 'waiting-list',
      pendingapproval: 'waiting-list',
      yettocheckin: 'yet-to-check-in',
      noshow: 'no-show',
      canceled: 'canceled',
      cancelled: 'canceled'
    };

    return statusMap[compactStatus] ?? 'yet-to-check-in';
  }

  /**
   * Maps API queue item to Participant interface
   */
  private mapQueueItemToParticipant(item: any): ExtendedParticipant {
    // Prefer explicit delivery status fields (PascalCase from Dapper or camelCase from JSON).
    const status = this.normalizeParticipantStatus(
      item?.deliveryStatus ??
        item?.DeliveryStatus ??
        this.resolveRawStatus(item)
    );

    // Extract userId - prefer userId field, fallback to id
    const userIdValue = item.userId ?? item.UserId ?? item.id ?? item.Id;
    const userId = userIdValue ? Number(userIdValue) : undefined;

    // Generate a fallback ID if neither id nor userId is available
    // Uses timestamp + counter + random for uniqueness and some determinism
    // Safe to use Math.random() here as this ID is only for UI tracking (Angular trackBy, internal state),
    // not for security, authentication, or API calls. The API uses userId, not this fallback ID.
    const generateFallbackId = (): string => {
      CheckInComponent.fallbackIdCounter++;
      return `fallback-${Date.now()}-${CheckInComponent.fallbackIdCounter}`;
    };
    

    return {
      id:
        item.id?.toString() ??
        item.Id?.toString() ??
        item.userId?.toString() ??
        item.UserId?.toString() ??
        generateFallbackId(),
      name: item.name ?? item.Name ?? item.userName ?? item.fullName ?? 'Unknown',
      identifier: item.identifier ?? item.documentId ?? item.idNumber ?? '',
      dob: item.dob ?? item.Dob ?? item.dateOfBirth ?? item.date_of_birth ?? null,
      transportType: item.transportType ?? item.TransportType ?? null,
      status: status,
      waitingListCount: item.count ?? item.Count ?? 0,
      userId: userId
    };
  }

  private get isTransportDelivery(): boolean {
    return this.deliveryType === 'transport';
  }

  private get isActivityDelivery(): boolean {
    return this.deliveryType === 'activity';
  }

  private get usesDedicatedDeliveryQueue(): boolean {
    return this.isTransportDelivery || this.isActivityDelivery;
  }

  /**
   * Fetches delivery queue from API
   */
  fetchDeliveryQueue(options: { refreshCounts?: boolean; showSkeleton?: boolean } = {}): void {
    const { refreshCounts = true, showSkeleton = true } = options;
    if (showSkeleton) {
      this.isLoading = true;
    }
    const programId = this.getProgramId();
    
    if (!programId) {
      console.error('Program ID not found');
      this.isLoading = false;
      return;
    }

    const location = getSelectedLocation();
    const payload: DeliveryQueuePayload = {
      programId: programId,
      statusFilter: this.selectedStatus === 'all' ? 'all' : this.mapStatusToApiFilter(this.selectedStatus),
      SearchText: this.searchValue || undefined,
      ...(location ? { locationId: location.id } : {}),
      ...(this.isActivityDelivery && this.selectedActivityId
        ? { activityId: this.selectedActivityId }
        : {})
    };

    const queueRequest$ = this.isActivityDelivery
      ? this.deliveryService.getActivityDeliveryQueue(payload)
      : this.isTransportDelivery
        ? this.deliveryService.getTransportDeliveryQueue(payload)
        : this.deliveryService.getDeliveryQueue(payload);

    queueRequest$.subscribe({
      next: (response) => {
        if (response?.statusCode === 200 && response?.queue) {
          this.participants = response.queue.map(item => this.mapQueueItemToParticipant(item));
        } else {
          this.participants = [];
        }
        this.isLoading = false;

        if (this.usesDedicatedDeliveryQueue && response?.counts) {
          this.applyTransportCounts(response.counts);
        } else if (refreshCounts) {
          this.fetchStatusCounts();
        }

        // Reset scroll and update active letter after data loads
        setTimeout(() => {
          const container = this.getContainer();
          if (container) {
            container.scrollTop = 0;
          }
          this.activeLetter = this.availableLetters[0] ?? 'A';
          this.updateActiveLetter();
        }, 100);
      },
      error: () => {
        this.participants = [];
        this.isLoading = false;
      }
    });
  }

  private applyTransportCounts(counts: Record<string, any>): void {
    this.statusCounts['yet-to-check-in'] = this.resolveCount(counts, ['yetToCheckIn', 'YetToCheckIn']);
    this.statusCounts['check-in'] = this.resolveCount(counts, ['checkIn', 'CheckIn', 'checkedIn']);
    this.statusCounts['delivered'] = this.resolveCount(counts, ['delivered', 'Delivered', 'deliveredCount']);
    this.statusCounts['not-delivered'] = this.resolveCount(counts, ['notDelivered', 'NotDelivered', 'not_delivered']);
    this.statusCounts['waiting-list'] = this.resolveCount(counts, ['todayPending', 'TodayPending', 'pending', 'Pending']);
    this.statusCounts['no-show'] = this.resolveCount(counts, ['noShow', 'NoShow', 'no_show']);
    this.statusCounts['canceled'] = this.resolveCount(counts, ['canceled', 'Canceled', 'cancelled', 'Cancelled']);
    const total = this.resolveCount(counts, ['total', 'all', 'overall']);
    const summed =
      this.statusCounts['yet-to-check-in'] +
      this.statusCounts['check-in'] +
      this.statusCounts['delivered'] +
      this.statusCounts['not-delivered'] +
      this.statusCounts['waiting-list'] +
      this.statusCounts['no-show'] +
      this.statusCounts['canceled'];
    this.statusCounts['all'] = total > 0 ? total : Math.max(summed, this.participants.length);
  }

  /**
   * Fetches the aggregated status counts used in the header buttons
   */
  private fetchStatusCounts(): void {
    const programId = this.getProgramId();
    
    if (!programId) {
      return;
    }

    // Transport / activity counts come from their dedicated queue responses.
    if (this.usesDedicatedDeliveryQueue) {
      const location = getSelectedLocation();
      const payload: DeliveryQueuePayload = {
        programId,
        statusFilter: 'all',
        ...(location ? { locationId: location.id } : {}),
        ...(this.isActivityDelivery && this.selectedActivityId
          ? { activityId: this.selectedActivityId }
          : {})
      };
      const countsRequest$ = this.isActivityDelivery
        ? this.deliveryService.getActivityDeliveryQueue(payload)
        : this.deliveryService.getTransportDeliveryQueue(payload);
      countsRequest$.subscribe({
        next: (response) => {
          if (response?.counts) {
            this.applyTransportCounts(response.counts);
          }
        },
        error: () => {
          // ignore count refresh errors
        }
      });
      return;
    }

    const location = getSelectedLocation();
    const countPayload: any = { programId: programId };
    if (location) {
      countPayload['locationId'] = location.id;
    }

    this.userService.getUsersCount(countPayload).subscribe({
      next: (response) => {
        const counts = response?.data?.[0]?.counts ?? {};
        this.statusCounts['yet-to-check-in'] = this.resolveCount(counts, [ 'pendingCount', 'yetToCheckIn']);
        this.statusCounts['check-in'] = this.resolveCount(counts, ['checkIn', 'checkedIn', 'checkin', 'checkedin']);
        this.statusCounts['delivered'] = this.resolveCount(counts, ['delivered', 'deliveredCount']);
        this.statusCounts['not-delivered'] = this.resolveCount(counts, ['notDelivered', 'NotDelivered', 'not_delivered']);
        this.statusCounts['waiting-list'] = this.resolveCount(counts, ['waitingList', 'waitinglist', 'waitList', 'waitlist', 'todayPending'],);
        this.statusCounts['no-show'] = this.resolveCount(counts, ['noShow', 'NoShow', 'no_show']);
        this.statusCounts['canceled'] = this.resolveCount(counts, ['canceled', 'Canceled', 'cancelled', 'Cancelled']);
        const total = this.resolveCount(counts, ['total', 'all', 'overall']);
        const summedStatuses =
          this.statusCounts['yet-to-check-in'] +
          this.statusCounts['check-in'] +
          this.statusCounts['delivered'] +
          this.statusCounts['not-delivered'] +
          this.statusCounts['waiting-list'] +
          this.statusCounts['no-show'] +
          this.statusCounts['canceled'];
        this.statusCounts['all'] = total > 0 ? total : Math.max(summedStatuses, this.statusCounts['all']);
      },
      error: () => {
        // Error fetching user counts
      }
    });
  }

  /**
   * Safely reads any of the provided keys from a loosely typed counts payload
   */
  private resolveCount(counts: Record<string, any>, keys: string[]): number {
    for (const key of keys) {
      if (counts?.[key] !== undefined && counts?.[key] !== null) {
        const parsed = Number(counts[key]);
        return Number.isFinite(parsed) ? parsed : 0;
      }
    }
    return 0;
  }

  getContainer(): HTMLElement | null {
    return this.contentContainer?.nativeElement ?? 
           (document.querySelector('.participants-content-container') as HTMLElement) ?? 
           null;
  }

  updateActiveLetter(): void {
    // Throttle scroll events for better performance
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }
    
    this.scrollTimeout = setTimeout(() => {
      this._updateActiveLetter();
    }, 50);
  }

  private _updateActiveLetter(): void {
    const container = this.getContainer();
    if (!container || this.isScrolling) return;

    const containerRect = container.getBoundingClientRect();
    const availableLetters = this.availableLetters;
    
    if (availableLetters.length === 0) return;

    // Find which letter section is currently visible at the top of the viewport
    let activeLetter = availableLetters[0];
    let minDistance = Infinity;
    const scrollTop = container.scrollTop;
    const threshold = 150; // Offset from top of container

    availableLetters.forEach(letter => {
      const element = document.getElementById(`letter-${letter}`);
      if (element) {
        const elementRect = element.getBoundingClientRect();
        const elementTop = scrollTop + (elementRect.top - containerRect.top);
        const distance = Math.abs(elementTop - scrollTop);
        
        // Check if the element is in the visible area (with threshold)
        if (elementTop <= scrollTop + threshold && elementTop >= scrollTop - 50) {
          if (distance < minDistance) {
            minDistance = distance;
            activeLetter = letter;
          }
        }
      }
    });

    // If no letter is in view, find the closest one above the viewport
    if (minDistance === Infinity) {
      availableLetters.forEach(letter => {
        const element = document.getElementById(`letter-${letter}`);
        if (element) {
          const elementRect = element.getBoundingClientRect();
          const elementTop = scrollTop + (elementRect.top - containerRect.top);
          const distance = Math.abs(elementTop - scrollTop);
          
          // Prefer letters that are above or at the viewport
          if (elementTop <= scrollTop + threshold) {
            if (distance < minDistance) {
              minDistance = distance;
              activeLetter = letter;
            }
          }
        }
      });
    }

    if (this.activeLetter !== activeLetter) {
      this.activeLetter = activeLetter;
    }
  }

  getStatusCount(status: string): number {
    return this.statusCounts[status] ?? 0;
  }

  getFilteredParticipants(): ExtendedParticipant[] {
    // API already filters by status and search, so return participants as-is
    // If needed, we can add client-side filtering here as a fallback
    return [...this.participants].sort((a, b) => a.name.localeCompare(b.name));
  }

  get groupedParticipants(): GroupedParticipants[] {
    const filtered = this.getFilteredParticipants();
    const grouped: { [key: string]: ExtendedParticipant[] } = {};

    filtered.forEach(participant => {
      const letter = participant.name[0].toUpperCase();
      if (!grouped[letter]) {
        grouped[letter] = [];
      }
      grouped[letter].push(participant);
    });

    return Object.keys(grouped)
      .sort((a, b) => a.localeCompare(b))
      .map(letter => ({
        letter,
        participants: grouped[letter] as Participant[]
      }));
  }

  get availableLetters(): string[] {
    return this.groupedParticipants.map(g => g.letter);
  }

  get waitingListParticipants(): ExtendedParticipant[] {
    if (this.selectedStatus !== 'waiting-list') {
      return [];
    }
    return this.getFilteredParticipants().sort((a, b) => {
      const aCount = a.waitingListCount ?? Number.POSITIVE_INFINITY;
      const bCount = b.waitingListCount ?? Number.POSITIVE_INFINITY;
      if (aCount !== bCount) {
        return aCount - bCount;
      }
      return a.name.localeCompare(b.name);
    });
  }


  isParticipantSelected(id: string): boolean {
    return this.selectedParticipants.includes(id);
  }

  isParticipantDelivering(id: string): boolean {
    return this.deliveringParticipantIds.has(id);
  }

  onParticipantClick(participant: ExtendedParticipant): void {
    // Deliveries are only allowed from Checked In or Waiting List
    if (this.selectedStatus !== 'check-in' && this.selectedStatus !== 'waiting-list') {
      return;
    }

    if (
      (participant.status !== 'check-in' && participant.status !== 'waiting-list') ||
      this.deliveringParticipantIds.has(participant.id)
    ) {
      return;
    }

    if (!participant.userId) {
      return;
    }

    // Ask staff: Delivered vs Not present (participant absent).
    this.pendingDeliveryParticipant = participant;
    this.cdr.markForCheck();
  }

  closeDeliveryActionModal(): void {
    if (this.pendingDeliveryParticipant && this.deliveringParticipantIds.has(this.pendingDeliveryParticipant.id)) {
      return;
    }
    this.pendingDeliveryParticipant = null;
  }

  confirmMarkDelivered(): void {
    const participant = this.pendingDeliveryParticipant;
    if (!participant) {
      return;
    }
    this.pendingDeliveryParticipant = null;
    this.submitDeliveryOutcome(participant, 'delivered');
  }

  confirmMarkNotPresent(): void {
    const participant = this.pendingDeliveryParticipant;
    if (!participant) {
      return;
    }
    this.pendingDeliveryParticipant = null;
    this.submitDeliveryOutcome(participant, 'not_delivered');
  }

  private submitDeliveryOutcome(
    participant: ExtendedParticipant,
    outcome: 'delivered' | 'not_delivered'
  ): void {
    const programId = this.getProgramId();
    if (!programId || !participant.userId) {
      return;
    }

    this.deliveringParticipantIds.add(participant.id);
    const previousStatus = participant.status;
    const uiStatus: Participant['status'] =
      outcome === 'delivered' ? 'delivered' : 'not-delivered';

    const deliverPayload = {
      userId: participant.userId,
      programId: programId,
      status: outcome,
      ...(this.isActivityDelivery && this.selectedActivityId
        ? { activityId: this.selectedActivityId }
        : {})
    };
    const deliverRequest$ = this.isActivityDelivery
      ? this.deliveryService.deliverActivity(deliverPayload)
      : this.isTransportDelivery
        ? this.deliveryService.deliverTransport(deliverPayload)
        : this.deliveryService.deliver(deliverPayload);

    deliverRequest$.subscribe({
      next: (response) => {
        if (response.statusCode === 200) {
          const updatedParticipant: ExtendedParticipant = {
            ...participant,
            status: uiStatus,
          };
          this.selectedStatus = uiStatus;
          this.participants = [updatedParticipant];
          this.bumpStatusCountsAfterOutcome(previousStatus, uiStatus);
          this.showDeliveryToast(participant, previousStatus, outcome);
          this.cdr.markForCheck();
          this.fetchDeliveryQueue({ refreshCounts: false, showSkeleton: false });
          this.fetchStatusCounts();
        }
        this.deliveringParticipantIds.delete(participant.id);
      },
      error: () => {
        this.deliveringParticipantIds.delete(participant.id);
      }
    });
  }

  /** Optimistic badge updates when a delivery outcome succeeds. */
  private bumpStatusCountsAfterOutcome(
    previousStatus: Participant['status'],
    nextStatus: Participant['status']
  ): void {
    if (previousStatus === 'check-in' && this.statusCounts['check-in'] > 0) {
      this.statusCounts['check-in'] -= 1;
    } else if (previousStatus === 'waiting-list' && this.statusCounts['waiting-list'] > 0) {
      this.statusCounts['waiting-list'] -= 1;
    }
    this.statusCounts[nextStatus] = (this.statusCounts[nextStatus] ?? 0) + 1;
  }

  handleDeliveryUndo(): void {
    if (!this.deliveryToast || this.deliveryToast.isUndoing) {
      return;
    }

    const programId = this.getProgramId();
    if (!programId) {
      return;
    }

    const toastState = this.deliveryToast;
    this.clearDeliveryToastTimeout();
    this.clearDeliveryToastCountdown();
    this.deliveryToast = { ...toastState, isUndoing: true };

    const undoPayload = {
      userId: toastState.userId,
      programId: programId,
      status: toastState.revertStatus === 'waiting-list' ? 'pending' : toastState.revertStatus,
      ...(this.isActivityDelivery && this.selectedActivityId
        ? { activityId: this.selectedActivityId }
        : {})
    };
    const undoRequest$ = this.isActivityDelivery
      ? this.deliveryService.deliverActivity(undoPayload)
      : this.isTransportDelivery
        ? this.deliveryService.deliverTransport(undoPayload)
        : this.deliveryService.deliver(undoPayload);

    undoRequest$.subscribe({
      next: (response) => {
        if (response.statusCode === 200) {
          this.fetchDeliveryQueue({ refreshCounts: false, showSkeleton: false });
          this.fetchStatusCounts();
          this.dismissDeliveryToast();
        } else {
          const remainingSeconds = Math.ceil(this.deliveryToastDuration / 1000);
          this.deliveryToast = {
            ...toastState,
            message: response.message ?? 'Unable to undo delivery. Please try again.',
            isUndoing: false,
            isHiding: false,
            remainingSeconds: remainingSeconds
          };
          this.startDeliveryToastTimer();
          this.startDeliveryToastCountdown();
        }
      },
      error: () => {
        const remainingSeconds = Math.ceil(this.deliveryToastDuration / 1000);
        this.deliveryToast = {
          ...toastState,
          message: 'Unable to undo delivery. Please try again.',
          isUndoing: false,
          isHiding: false,
          remainingSeconds: remainingSeconds
        };
        this.startDeliveryToastTimer();
        this.startDeliveryToastCountdown();
      }
    });
  }

  dismissDeliveryToast(animated: boolean = true): void {
    if (!this.deliveryToast) {
      this.clearDeliveryToastTimeout();
      this.clearDeliveryToastCountdown();
      return;
    }

    this.clearDeliveryToastTimeout();
    this.clearDeliveryToastCountdown();

    if (!animated) {
      this.deliveryToast = null;
      this.clearDeliveryToastHideTimeout();
      return;
    }

    if (this.deliveryToast.isHiding) {
      return;
    }

    this.deliveryToast = {
      ...this.deliveryToast,
      isHiding: true
    };

    this.clearDeliveryToastHideTimeout();
    this.deliveryToastHideTimeoutId = setTimeout(() => {
      this.deliveryToast = null;
      this.clearDeliveryToastHideTimeout();
    }, 250);
  }

  private showDeliveryToast(
    participant: ExtendedParticipant,
    revertStatus: Participant['status'],
    outcome: 'delivered' | 'not_delivered' = 'delivered'
  ): void {
    if (!participant.userId) {
      return;
    }

    const initialSeconds = Math.ceil(this.deliveryToastDuration / 1000);
    this.deliveryToast = {
      participantId: participant.id,
      participantName: participant.name,
      userId: participant.userId,
      revertStatus,
      message:
        outcome === 'not_delivered'
          ? this.languageService.t('checkin.toast.notDelivered')
          : this.languageService.t('checkin.toast.delivered'),
      isUndoing: false,
      isHiding: false,
      remainingSeconds: initialSeconds,
      outcome
    };
    this.startDeliveryToastTimer();
    this.startDeliveryToastCountdown();
  }

  private startDeliveryToastTimer(): void {
    this.clearDeliveryToastTimeout();
    if (this.deliveryToast?.isHiding) {
      this.deliveryToast = { ...this.deliveryToast, isHiding: false };
    }
    this.deliveryToastTimeoutId = setTimeout(() => this.dismissDeliveryToast(), this.deliveryToastDuration);
  }

  private startDeliveryToastCountdown(): void {
    this.clearDeliveryToastCountdown();
    if (!this.deliveryToast) {
      return;
    }
    this.deliveryToastCountdownIntervalId = setInterval(() => {
      if (this.deliveryToast && !this.deliveryToast.isUndoing && !this.deliveryToast.isHiding) {
        if (this.deliveryToast.remainingSeconds > 0) {
          this.deliveryToast = {
            ...this.deliveryToast,
            remainingSeconds: this.deliveryToast.remainingSeconds - 1
          };
        } else {
          this.clearDeliveryToastCountdown();
        }
      } else {
        this.clearDeliveryToastCountdown();
      }
    }, 1000);
  }

  private clearDeliveryToastCountdown(): void {
    if (this.deliveryToastCountdownIntervalId) {
      clearInterval(this.deliveryToastCountdownIntervalId);
      this.deliveryToastCountdownIntervalId = null;
    }
  }

  private clearDeliveryToastTimeout(): void {
    if (this.deliveryToastTimeoutId) {
      clearTimeout(this.deliveryToastTimeoutId);
      this.deliveryToastTimeoutId = null;
    }
  }

  private clearDeliveryToastHideTimeout(): void {
    if (this.deliveryToastHideTimeoutId) {
      clearTimeout(this.deliveryToastHideTimeoutId);
      this.deliveryToastHideTimeoutId = null;
    }
  }

  scrollToLetter(letter: string): void {
    if (!this.availableLetters.includes(letter)) {
      return;
    }
    
    // Update active letter immediately for better UX
    this.activeLetter = letter;
    this.isScrolling = true;
    
    // Use requestAnimationFrame and setTimeout to ensure the view is updated
    requestAnimationFrame(() => {
      setTimeout(() => {
        const element = document.getElementById(`letter-${letter}`);
        if (!element) {
          this.isScrolling = false;
          return;
        }
        
        const container = this.getContainer();
        if (!container) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setTimeout(() => {
            this.isScrolling = false;
            this.updateActiveLetter();
          }, 500);
          return;
        }
        
        const containerRect = container.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        
        // Calculate the scroll position relative to the container with proper offset
        const scrollTop = container.scrollTop + (elementRect.top - containerRect.top) - 10; // 10px offset from top
        
        container.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth'
        });
        
        // Update active letter after scroll completes
        setTimeout(() => {
          this.isScrolling = false;
          this._updateActiveLetter();
        }, 600);
      }, 50);
    });
  }

  handleAlphabetKeyDown(event: KeyboardEvent, letter: string): void {
    // Handle Enter and Space keys for keyboard navigation
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (this.availableLetters.includes(letter)) {
        this.scrollToLetter(letter);
      }
    }
  }

  isLetterActive(letter: string): boolean {
    return this.availableLetters.includes(letter) && this.activeLetter === letter;
  }

  getStatusStyle(status: string): StatusStyle {
    const styleMap: Record<string, StatusStyle> = {
      'all': { bg: '#F2F5F7', text: '#22252B', border: '#D0D1D4' },
      'yet-to-check-in': { bg: '#ECF7FB', text: '#42B0D5', border: '#42B0D5', badgeColor: '#42B0D5' },
      'check-in': { bg: '#EAF4FE', text: '#2E90FA', border: '#2E90FA', badgeColor: '#2E90FA' },
      'delivered': { bg: '#ECF7EB', text: '#40AB35', border: '#40AB35', badgeColor: '#40AB35' },
      'not-delivered': { bg: '#FEF3F2', text: '#B42318', border: '#B42318', badgeColor: '#B42318' },
      'waiting-list': { bg: '#FEF1EA', text: '#F26F24', border: '#F26F24', badgeColor: '#F26F24' },
      'no-show': { bg: '#F4F3FF', text: '#7A5AF8', border: '#7A5AF8', badgeColor: '#7A5AF8' },
      'canceled': { bg: '#F2F4F7', text: '#667085', border: '#667085', badgeColor: '#667085' }
    };
    return styleMap[status] ?? { bg: '#F2F5F7', text: '#22252B', border: '#D0D1D4' };
  }

  /**
   * Gets the status button configuration for a given status
   */
  getStatusButtonConfig(status: string): { bg: string; textColor: string; borderColor: string; badgeColor?: string } {
    const isSelected = this.selectedStatus === status;
    const style = this.getStatusStyle(status);
    return {
      bg: isSelected ? style.bg : '#F2F5F7',
      textColor: isSelected ? style.text : '#22252B',
      borderColor: isSelected ? style.border : '#D0D1D4',
      badgeColor: style.badgeColor
    };
  }

  onStatusChange(status: string): void {
    if (!status || status === this.selectedStatus) {
      return;
    }
    this.selectedStatus = status;
    this.cdr.markForCheck();
    // Fetch data with new status filter
    this.fetchDeliveryQueue();
  }

  onSearchChange(value: string): void {
    this.searchValue = value;
    
    // Debounce search to avoid too many API calls
    if (this.searchDebounceTimeout) {
      clearTimeout(this.searchDebounceTimeout);
    }
    
    this.searchDebounceTimeout = setTimeout(() => {
      this.fetchDeliveryQueue();
    }, 500); // Wait 500ms after user stops typing
  }

  trackByParticipant(index: number, participant: Participant): string {
    return participant.id;
  }

  trackByLetter(index: number, group: GroupedParticipants): string {
    return group.letter;
  }

  trackByIndex(index: number): number {
    return index;
  }

  /**
   * Checks if there's no data to display
   */
  get hasNoData(): boolean {
    if (this.isLoading) {
      return false;
    }
    if (this.selectedStatus === 'waiting-list') {
      return this.waitingListParticipants.length === 0;
    }
    return this.groupedParticipants.length === 0;
  }

  /**
   * Gets the display label for a status
   */
  getStatusLabel(status: string): string {
    const match = this.statusConfigs.find((c) => c.key === status);
    return match?.label ?? status;
  }

  /**
   * Gets the empty state message based on the selected filter
   */
  getEmptyStateMessage(): string {
    if (this.searchValue) {
      return this.languageService.t('checkin.empty.search', { query: this.searchValue });
    }

    const emptyKeys: { [key: string]: string } = this.isActivityDelivery
      ? {
          'check-in': 'checkin.empty.activityCheckIn',
          'waiting-list': 'checkin.empty.activityWaiting',
          'yet-to-check-in': 'checkin.empty.activityYetToCheckIn',
          'all': 'checkin.empty.activityAll',
          'delivered': 'checkin.empty.activityDelivered',
          'not-delivered': 'checkin.empty.notDelivered',
        }
      : this.isTransportDelivery
        ? {
            'check-in': 'checkin.empty.transportCheckIn',
            'waiting-list': 'checkin.empty.transportWaiting',
            'yet-to-check-in': 'checkin.empty.transportYetToCheckIn',
            'all': 'checkin.empty.transportAll',
            'delivered': 'checkin.empty.transportDelivered',
            'not-delivered': 'checkin.empty.notDelivered',
          }
        : {
            'check-in': 'checkin.empty.checkIn',
            'waiting-list': 'checkin.empty.waitingList',
            'yet-to-check-in': 'checkin.empty.yetToCheckIn',
            'all': 'checkin.empty.all',
            'delivered': 'checkin.empty.delivered',
            'not-delivered': 'checkin.empty.notDelivered',
            'no-show': 'checkin.empty.noShow',
            'canceled': 'checkin.empty.canceled',
          };

    const key = emptyKeys[this.selectedStatus];
    if (key) {
      return this.languageService.t(key);
    }
    return this.languageService.t('checkin.empty.generic', {
      status: this.getStatusLabel(this.selectedStatus),
    });
  }
}
